import { useState } from "react";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  FormField,
  LoadingButton,
  SelectField,
  SearchableSelect,
  MultiSelect,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/common";
import {
  AlertCircle,
  CheckCircle2,
  Component,
  Info,
  Terminal,
} from "lucide-react";

const ComponentsShowcase = observer(() => {
  const [switchState, setSwitchState] = useState(false);
  const [checkboxState, setCheckboxState] = useState(false);
  const [radioValue, setRadioValue] = useState("option1");
  const [selectValue, setSelectValue] = useState("");
  const [selectFieldValue, setSelectFieldValue] = useState("");
  const [searchableValue, setSearchableValue] = useState("");
  const [multiSelectValue, setMultiSelectValue] = useState([]);

  // Options for SelectField (using key/value pattern like nxplan)
  const selectFieldOptions = [
    { key: "option1", value: "Option 1" },
    { key: "option2", value: "Option 2" },
    { key: "option3", value: "Option 3" },
    { key: "option4", value: "Option 4" },
  ];

  // Options for searchable and multi-select
  const selectOptions = [
    { value: "react", label: "React" },
    { value: "vue", label: "Vue" },
    { value: "angular", label: "Angular" },
    { value: "svelte", label: "Svelte" },
    { value: "nextjs", label: "Next.js" },
    { value: "nuxtjs", label: "Nuxt.js" },
    { value: "gatsby", label: "Gatsby" },
    { value: "remix", label: "Remix" },
  ];

  // Handler for SelectField (receives synthetic event)
  const handleSelectFieldChange = (e) => {
    setSelectFieldValue(e.target.value);
  };

  return (
    <div className="space-y-8 pb-12">
      <PageHeader
        title="Components Showcase"
        description="Complete collection of shadcn/ui components with NxStart theme"
        icon={Component}
      />

      {/* Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Buttons</CardTitle>
          <CardDescription>Different button variants and sizes</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="mb-3 text-sm font-medium">Variants</p>
            <div className="flex flex-wrap gap-2">
              <Button>Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
              <Button variant="destructive">Destructive</Button>
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Sizes</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
              <Button size="icon">
                <CheckCircle2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Loading Button (Custom)</p>
            <div className="flex gap-2">
              <LoadingButton loading>Loading...</LoadingButton>
              <LoadingButton>Not Loading</LoadingButton>
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Disabled</p>
            <div className="flex gap-2">
              <Button disabled>Disabled</Button>
              <Button variant="secondary" disabled>
                Disabled Secondary
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Inputs */}
      <Card>
        <CardHeader>
          <CardTitle>Form Inputs</CardTitle>
          <CardDescription>
            Input fields, textarea, and form field wrapper
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="input-default">Default Input</Label>
              <Input id="input-default" placeholder="Enter text..." />
            </div>

            <div className="space-y-2">
              <Label htmlFor="input-email">Email Input</Label>
              <Input
                id="input-email"
                type="email"
                placeholder="email@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="input-password">Password Input</Label>
              <Input
                id="input-password"
                type="password"
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="input-disabled">Disabled Input</Label>
              <Input id="input-disabled" disabled placeholder="Disabled" />
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="textarea">Textarea</Label>
            <Textarea
              id="textarea"
              placeholder="Type your message here..."
              rows={4}
            />
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">
              FormField (Custom Wrapper)
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <FormField label="Email" placeholder="you@example.com" required />
              <FormField
                label="Username"
                placeholder="johndoe"
                error="This field is required"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Select, Checkbox, Radio, Switch */}
      <Card>
        <CardHeader>
          <CardTitle>Select & Toggles</CardTitle>
          <CardDescription>
            Dropdowns, checkboxes, radio buttons, and switches
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label>Select Dropdown</Label>
            <SelectField
              options={selectFieldOptions}
              value={selectValue}
              onValueChange={setSelectValue}
              placeholder="Select an option"
            />
            {selectValue && (
              <p className="text-sm text-muted-foreground">
                Selected: {selectValue}
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <p className="text-sm font-medium">SelectField</p>
            <SelectField
              options={selectFieldOptions}
              value={selectFieldValue}
              onChange={handleSelectFieldChange}
              placeholder="Choose an option"
              label="Select Option"
              required
            />
            {selectFieldValue && (
              <p className="text-sm text-muted-foreground">
                Selected key: {selectFieldValue} (
                {
                  selectFieldOptions.find((opt) => opt.key === selectFieldValue)
                    ?.value
                }
                )
              </p>
            )}
          </div>

          <Separator />

          <div className="space-y-3">
            <Label>Checkboxes</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="check1"
                checked={checkboxState}
                onCheckedChange={setCheckboxState}
              />
              <Label htmlFor="check1" className="cursor-pointer font-normal">
                Accept terms and conditions
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="check2" />
              <Label htmlFor="check2" className="cursor-pointer font-normal">
                Subscribe to newsletter
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="check3" disabled />
              <Label htmlFor="check3" className="cursor-pointer font-normal">
                Disabled checkbox
              </Label>
            </div>
          </div>

          <Separator />

          <div className="space-y-3">
            <Label>Radio Group</Label>
            <RadioGroup value={radioValue} onValueChange={setRadioValue}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option1" id="r1" />
                <Label htmlFor="r1" className="cursor-pointer font-normal">
                  Option 1
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option2" id="r2" />
                <Label htmlFor="r2" className="cursor-pointer font-normal">
                  Option 2
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="option3" id="r3" />
                <Label htmlFor="r3" className="cursor-pointer font-normal">
                  Option 3
                </Label>
              </div>
            </RadioGroup>
            <p className="text-sm text-muted-foreground">
              Selected: {radioValue}
            </p>
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="switch-1">Switch Component</Label>
              <p className="text-sm text-muted-foreground">
                Toggle this switch on and off
              </p>
            </div>
            <Switch
              id="switch-1"
              checked={switchState}
              onCheckedChange={setSwitchState}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Switch is: {switchState ? "ON" : "OFF"}
          </p>
        </CardContent>
      </Card>

      {/* Searchable Select */}
      <Card>
        <CardHeader>
          <CardTitle>Searchable Select</CardTitle>
          <CardDescription>
            Combobox with search functionality (Custom Component)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Framework</Label>
            <SearchableSelect
              options={selectOptions}
              value={searchableValue}
              onChange={setSearchableValue}
              placeholder="Select a framework..."
              searchPlaceholder="Search frameworks..."
              emptyText="No framework found."
            />
            {searchableValue && (
              <p className="text-sm text-muted-foreground">
                Selected:{" "}
                {
                  selectOptions.find((opt) => opt.value === searchableValue)
                    ?.label
                }
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Multi Select */}
      <Card>
        <CardHeader>
          <CardTitle>Multi Select</CardTitle>
          <CardDescription>
            Select multiple options with badges (Custom Component)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Select Technologies</Label>
            <MultiSelect
              options={selectOptions}
              value={multiSelectValue}
              onChange={setMultiSelectValue}
              placeholder="Select technologies..."
              searchPlaceholder="Search technologies..."
              emptyText="No technology found."
              maxDisplay={3}
            />
            {multiSelectValue.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Selected ({multiSelectValue.length}):{" "}
                {multiSelectValue
                  .map(
                    (v) => selectOptions.find((opt) => opt.value === v)?.label,
                  )
                  .join(", ")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Table</CardTitle>
          <CardDescription>Data tables with rows and columns</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableCaption>A list of recent invoices</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Invoice</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">INV001</TableCell>
                <TableCell>
                  <Badge className="bg-green-600">Paid</Badge>
                </TableCell>
                <TableCell>Credit Card</TableCell>
                <TableCell className="text-right">$250.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">INV002</TableCell>
                <TableCell>
                  <Badge variant="secondary">Pending</Badge>
                </TableCell>
                <TableCell>PayPal</TableCell>
                <TableCell className="text-right">$150.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">INV003</TableCell>
                <TableCell>
                  <Badge className="bg-green-600">Paid</Badge>
                </TableCell>
                <TableCell>Bank Transfer</TableCell>
                <TableCell className="text-right">$350.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">INV004</TableCell>
                <TableCell>
                  <Badge variant="destructive">Failed</Badge>
                </TableCell>
                <TableCell>Credit Card</TableCell>
                <TableCell className="text-right">$450.00</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="font-medium">INV005</TableCell>
                <TableCell>
                  <Badge className="bg-green-600">Paid</Badge>
                </TableCell>
                <TableCell>PayPal</TableCell>
                <TableCell className="text-right">$550.00</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Alerts</CardTitle>
          <CardDescription>
            Different alert variants for notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Terminal className="h-4 w-4" />
            <AlertTitle>Default Alert</AlertTitle>
            <AlertDescription>
              This is a default alert with an icon and description.
            </AlertDescription>
          </Alert>

          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Alert</AlertTitle>
            <AlertDescription>
              This is a destructive alert. Use it for errors or warnings.
            </AlertDescription>
          </Alert>

          <Alert className="border-green-500 text-green-900 dark:text-green-100">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Success Alert</AlertTitle>
            <AlertDescription>
              This is a custom success alert using color overrides.
            </AlertDescription>
          </Alert>

          <Alert className="border-blue-500 text-blue-900 dark:text-blue-100">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertTitle>Info Alert</AlertTitle>
            <AlertDescription>
              This is an informational alert for general messages.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Badges */}
      <Card>
        <CardHeader>
          <CardTitle>Badges</CardTitle>
          <CardDescription>Status indicators and labels</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="mb-3 text-sm font-medium">Variants</p>
            <div className="flex flex-wrap gap-2">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="destructive">Destructive</Badge>
            </div>
          </div>

          <Separator />

          <div>
            <p className="mb-3 text-sm font-medium">Custom Colors</p>
            <div className="flex flex-wrap gap-2">
              <Badge className="bg-green-600 hover:bg-green-700">Success</Badge>
              <Badge className="bg-blue-600 hover:bg-blue-700">Info</Badge>
              <Badge className="bg-yellow-600 hover:bg-yellow-700">
                Warning
              </Badge>
              <Badge className="bg-purple-600 hover:bg-purple-700">
                Purple
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cards */}
      <Card>
        <CardHeader>
          <CardTitle>Card Component</CardTitle>
          <CardDescription>
            Container component with header, content, and footer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Simple Card</CardTitle>
                <CardDescription>Card with header and content</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This is the card content area. You can put any content here.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Card with Footer</CardTitle>
                <CardDescription>Includes a footer section</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Card content goes here.
                </p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="ghost">Cancel</Button>
                <Button>Save</Button>
              </CardFooter>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Card>
        <CardHeader>
          <CardTitle>Dialog (Modal)</CardTitle>
          <CardDescription>
            Modal dialogs for overlays and confirmations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Dialog>
            <DialogTrigger asChild>
              <Button>Open Dialog</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Dialog Title</DialogTitle>
                <DialogDescription>
                  This is a dialog description. You can put any content inside
                  the dialog.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <FormField label="Name" placeholder="Enter your name" />
                <FormField
                  label="Email"
                  type="email"
                  placeholder="Enter your email"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline">Cancel</Button>
                <Button>Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Skeleton */}
      <Card>
        <CardHeader>
          <CardTitle>Skeleton</CardTitle>
          <CardDescription>Loading placeholders</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-12 w-12 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-[250px]" />
              <Skeleton className="h-4 w-[200px]" />
            </div>
          </div>

          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Skeleton className="h-32 w-full rounded-radius" />
            <Skeleton className="h-32 w-full rounded-radius" />
            <Skeleton className="h-32 w-full rounded-radius" />
          </div>
        </CardContent>
      </Card>

      {/* Separator */}
      <Card>
        <CardHeader>
          <CardTitle>Separator</CardTitle>
          <CardDescription>Visual dividers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm">Content above</p>
            <Separator className="my-4" />
            <p className="text-sm">Content below</p>
          </div>

          <div className="flex h-20 items-center">
            <p className="text-sm">Left</p>
            <Separator orientation="vertical" className="mx-4" />
            <p className="text-sm">Right</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

export default ComponentsShowcase;
